import React, { useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  IconButton,
  Image,
  Link,
  SimpleGrid,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaEye, FaEyeSlash, FaEnvelopeOpenText } from "react-icons/fa";
import { supabase } from "lib/supabase";
import logoNotitia from "assets/img/logo-notitia.png";

// Logos de marque (SVG officiels)
function GoogleIcon() {
  return (
    <Box as="svg" viewBox="0 0 48 48" w="18px" h="18px" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </Box>
  );
}
function MicrosoftIcon() {
  return (
    <Box as="svg" viewBox="0 0 23 23" w="16px" h="16px" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </Box>
  );
}

function SignUp() {
  const history = useHistory();
  const toast = useToast();
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [entreprise, setEntreprise] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [erreur, setErreur] = useState("");
  const [info, setInfo] = useState("");
  const [chargement, setChargement] = useState(false);
  const { isOpen: confirmOpen, onOpen: openConfirm, onClose: closeConfirm } = useDisclosure();

  async function inscription() {
    setErreur(""); setInfo("");
    if (!prenom.trim() || !nom.trim()) { setErreur("Renseignez votre prénom et votre nom."); return; }
    if (!entreprise.trim()) { setErreur("Renseignez le nom de votre entreprise."); return; }
    if (!email || !password) { setErreur("Renseignez un e-mail et un mot de passe."); return; }
    if (password.length < 8) { setErreur("Le mot de passe doit contenir au moins 8 caractères."); return; }
    setChargement(true);
    const fullName = `${prenom.trim()} ${nom.trim()}`.trim();
    // Ces métadonnées configurent le compte : profil (prénom/nom) + organisation (entreprise)
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(), password,
      options: { data: { full_name: fullName, first_name: prenom.trim(), last_name: nom.trim(), company: entreprise.trim() } },
    });
    setChargement(false);
    if (error) {
      // Supabase renvoie parfois l'erreur directement (selon la config)
      if (/already registered|already been registered|user already exists/i.test(error.message || "")) {
        setErreur("Un compte existe déjà avec cette adresse e-mail. Connectez-vous ou réinitialisez votre mot de passe.");
        return;
      }
      setErreur(error.message || "Inscription impossible."); return;
    }
    // Adresse déjà utilisée : pour ne pas divulguer l'existence du compte, Supabase renvoie
    // un utilisateur SANS identités et n'envoie aucun e-mail. On l'indique clairement à l'utilisateur.
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setErreur("Un compte existe déjà avec cette adresse e-mail. Connectez-vous ou réinitialisez votre mot de passe.");
      return;
    }
    if (data.session) {
      history.push("/admin/dashboard"); // session active : le contexte crée l'organisation « entreprise »
    } else {
      setInfo("Compte créé. Vérifiez votre e-mail pour confirmer, puis connectez-vous.");
      openConfirm();
    }
  }

  async function oauth(provider, label) {
    const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo: window.location.origin } });
    if (error) toast({ title: `Inscription ${label}`, description: "Fournisseur à activer dans Supabase (Authentication → Providers).", status: "info", duration: 3000 });
  }

  const champStyle = {
    bg: "gray.50",
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "12px",
    fontSize: "sm",
    h: "46px",
    _hover: { borderColor: "gray.300" },
    _focus: { borderColor: "teal.300", boxShadow: "0 0 0 1px #4FD1C5", bg: "white" },
  };

  // Libellé avec barre verticale turquoise
  const Label = ({ children }) => (
    <Flex align="center" mb="6px">
      <Box w="3px" h="14px" bg="teal.400" borderRadius="full" me="8px" flexShrink={0} />
      <Text fontSize="sm" fontWeight="bold" color="gray.700">{children}</Text>
    </Flex>
  );

  const boutonSocial = (icon, label, provider) => (
    <Button
      flex="1"
      onClick={() => oauth(provider, label)}
      bg="white"
      color="gray.700"
      borderRadius="12px"
      border="1px solid"
      borderColor="gray.200"
      h="48px"
      fontSize="sm"
      fontWeight="bold"
      leftIcon={icon}
      _hover={{ borderColor: "teal.300", transform: "translateY(-2px)", boxShadow: "0 8px 18px rgba(49,151,149,0.15)" }}
      transition="all .15s"
    >
      {label}
    </Button>
  );

  return (
    <Flex minH="100vh" align="center" justify="center" bgGradient="linear(135deg, #E5FBF7 0%, #A9EDE4 60%, #7FE3D6 100%)" p={{ base: "16px", md: "32px" }}>
      {/* Grand panneau translucide */}
      <Box position="relative" w="100%" maxW="1180px" borderRadius="34px" bg="whiteAlpha.400" backdropFilter="blur(6px)" boxShadow="0 30px 80px rgba(45,116,110,0.25)" p={{ base: "16px", md: "36px" }} overflow="hidden">
        <Flex direction={{ base: "column", lg: "row" }} align="center" justify="center" gap={{ base: "20px", lg: "10px" }}>
          {/* Carte d'inscription */}
          <Box bg="white" borderRadius="24px" p={{ base: "28px", md: "40px" }} w={{ base: "100%", lg: "580px" }} flexShrink={0} boxShadow="0 20px 50px rgba(45,116,110,0.15)">
            <Flex align="center" mb="10px">
              <Box w="6px" h="38px" bg="teal.400" borderRadius="full" me="12px" flexShrink={0} />
              <Heading color="gray.800" fontSize="40px" fontWeight="bold" lineHeight="1">Inscription</Heading>
            </Flex>
            <Text fontSize="sm" color="gray.500" mb="24px" lineHeight="1.5">
              Créez votre compte et lancez votre première veille média en quelques minutes.
            </Text>

            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="14px" mb="18px">
              <Box>
                <Label>Prénom</Label>
                <Input {...champStyle} type="text" placeholder="Prénom" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
              </Box>
              <Box>
                <Label>Nom</Label>
                <Input {...champStyle} type="text" placeholder="Nom" value={nom} onChange={(e) => setNom(e.target.value)} />
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="14px" mb="18px">
              <Box>
                <Label>Nom de l'entreprise</Label>
                <Input {...champStyle} type="text" placeholder="Votre société" value={entreprise} onChange={(e) => setEntreprise(e.target.value)} />
              </Box>
              <Box>
                <Label>E-mail</Label>
                <Input {...champStyle} type="email" placeholder="vous@entreprise.fr" value={email} onChange={(e) => setEmail(e.target.value)} />
              </Box>
            </SimpleGrid>

            <Label>Mot de passe</Label>
            <InputGroup>
              <Input
                {...champStyle}
                type={showPwd ? "text" : "password"}
                placeholder="8 caractères minimum"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") inscription(); }}
              />
              <InputRightElement h="46px">
                <IconButton aria-label="Afficher le mot de passe" variant="ghost" size="sm" color="gray.400" icon={showPwd ? <FaEyeSlash /> : <FaEye />} onClick={() => setShowPwd((v) => !v)} _hover={{ color: "teal.400", bg: "transparent" }} />
              </InputRightElement>
            </InputGroup>

            {erreur && <Text color="red.400" fontSize="sm" mt="12px">{erreur}</Text>}
            {info && <Text color="teal.500" fontSize="sm" mt="12px">{info}</Text>}

            <Button
              w="100%"
              mt="20px"
              h="46px"
              bg="teal.400"
              color="white"
              borderRadius="12px"
              fontWeight="bold"
              isLoading={chargement}
              _hover={{ bg: "teal.500" }}
              _active={{ bg: "teal.600" }}
              onClick={inscription}
            >
              Créer mon compte
            </Button>

            <Flex align="center" my="18px" gap="12px">
              <Box flex="1" h="1px" bg="gray.200" />
              <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">Ou s'inscrire avec</Text>
              <Box flex="1" h="1px" bg="gray.200" />
            </Flex>

            <Flex gap="12px">
              {boutonSocial(<GoogleIcon />, "Google", "google")}
              {boutonSocial(<MicrosoftIcon />, "Microsoft", "azure")}
            </Flex>

            <Flex justify="center" mt="24px">
              <Text fontSize="sm" color="gray.500">
                Vous avez déjà un compte ?{" "}
                <Link as={NavLink} to="/auth/signin" color="teal.500" fontWeight="bold">Connectez-vous</Link>
              </Text>
            </Flex>
          </Box>

          {/* Logo de marque en grand */}
          <Flex flex="1" align="center" justify="center" minH={{ base: "200px", lg: "460px" }} position="relative" w="100%">
            <Image src={logoNotitia} alt="notitia" w={{ base: "78%", lg: "82%" }} maxW="460px" objectFit="contain" filter="drop-shadow(0 20px 34px rgba(45,116,110,0.25))" />
          </Flex>
        </Flex>
      </Box>

      {/* Pop-up de confirmation d'inscription */}
      <Modal isOpen={confirmOpen} onClose={closeConfirm} isCentered size="md">
        <ModalOverlay />
        <ModalContent borderRadius="20px" p="8px">
          <ModalBody textAlign="center" pt="30px" pb="10px">
            <Flex w="66px" h="66px" borderRadius="20px" bg="teal.50" align="center" justify="center" mx="auto" mb="18px">
              <Icon as={FaEnvelopeOpenText} color="teal.400" fontSize="26px" />
            </Flex>
            <Heading fontSize="xl" color="gray.800" mb="8px">Vérifiez votre e-mail</Heading>
            <Text fontSize="sm" color="gray.500" lineHeight="1.6">
              Votre compte a été créé. Un e-mail de confirmation vient de vous être envoyé
              {email ? <> à <Text as="span" fontWeight="bold" color="gray.700">{email}</Text></> : null}. Cliquez sur le lien qu'il contient, puis connectez-vous.
            </Text>
          </ModalBody>
          <ModalFooter justifyContent="center" pb="26px">
            <Button bg="teal.400" color="white" _hover={{ bg: "teal.500" }} borderRadius="12px" px="28px" onClick={() => { closeConfirm(); history.push("/auth/signin"); }}>
              Aller à la connexion
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default SignUp;
